import { isFeatureAllowedForICPAccount } from 'modules/accounts/utils/isFeatureAllowedForICPAccount';
import { urlValidations } from 'modules/campaigns/config';
import {
    ContentCellRenderer,
    CreativeIdCellRenderer,
    CreativeLibraryActionsCellRenderer,
    CreativeLibraryStateCellRenderer,
    EllipsisCellRenderer,
    FormattedMessageHeaderRenderer,
    IMAGE_CELL_RENDERER_MODES,
    ImageCellRenderer,
    withCreativeEditIndication,
} from 'modules/campaigns/modules/campaigns-reports/components';
import { AssignmentCellRenderer } from 'modules/campaigns/modules/campaigns-reports/components/CellRenderers/AssignmentCellRenderer';
import {
    actionsColumnType,
    bypassMetadataFilterType,
    editableColumn,
    keyColumn,
    leftPinnedColumn,
    lockedColumn,
    numericColumn,
    rightPinnedColumn,
    staticColumn,
    toggleColumn,
} from 'modules/campaigns/modules/campaigns-reports/config/columns';
import * as FIELDS from 'modules/campaigns/modules/campaigns-reports/config/fields';
import {
    defaultHeaderComponentParams,
    nonDefaultHeaderComponentParams,
} from 'modules/campaigns/modules/campaigns-reports/config/fields';
import { isCreativeRejected, isHTMLCardCreative } from 'modules/campaigns/modules/campaigns-reports/config/predicates';
import { updateLibraryCreative } from 'modules/campaigns/modules/campaigns-reports/flows';
import { updateLibraryCreativeUrlCellEditor } from 'modules/campaigns/modules/campaigns-reports/flows/updateLibraryCreativeUrlCellEditor';
import { normalizeColumnDefs } from 'modules/campaigns/modules/campaigns-reports/utils';
import { BYPASS_URL_RESPONSE_VALIDATION_FIELD } from 'modules/campaigns/modules/creative-creator/components/UrlList/UrlValidatedInput';
import { deleteLibraryCreative } from 'modules/creative-library/flows';
import { duplicateLibraryCreative } from 'modules/creative-library/flows/duplicateLibraryCreative';
import { editLibraryCreative } from 'modules/creative-library/flows/editLibraryCreative';
import { previewLibraryCreative } from 'modules/creative-library/flows/previewLibraryCreative';
import { FEATURE_FLAGS } from 'modules/taboola-common-frontend-modules/account-configurations';
import { CREATIVE_LIBRARY_REPORT_ID } from './creativeLibraryReportId';

const creativeLibraryReportColumnDefinition = [
    {
        headerName: FIELDS.IS_ACTIVE.label,
        field: FIELDS.IS_ACTIVE.field,
        type: [lockedColumn, toggleColumn, leftPinnedColumn, staticColumn],
        cellRendererParams: {
            ...FIELDS.IS_ACTIVE.renderParams,
            isDisabled: data => isCreativeRejected(data),
            reportId: CREATIVE_LIBRARY_REPORT_ID,
        },
        cellRenderer: CreativeLibraryStateCellRenderer,
        valueChangeHandler: updateLibraryCreative,
        csv: false,
    },
    {
        headerName: FIELDS.THUMBNAIL_URL.label,
        headerValueGetter: FormattedMessageHeaderRenderer,
        headerComponentParams: FIELDS.THUMBNAIL_URL.headerComponentParams,
        field: FIELDS.THUMBNAIL_URL.field,
        type: [leftPinnedColumn, bypassMetadataFilterType],
        valueGetter: ({ data }) => {
            const { thumbnailUrl } = data;
            return thumbnailUrl;
        },
        cellRenderer: ImageCellRenderer,
        cellRendererParams: {
            mode: IMAGE_CELL_RENDERER_MODES.DEFAULT,
        },
        width: 120,
        csv: FIELDS.THUMBNAIL_URL.csv,
    },
    {
        headerName: FIELDS.CUSTOM_CREATIVE_NAME.label,
        headerValueGetter: FormattedMessageHeaderRenderer,
        headerComponentParams: nonDefaultHeaderComponentParams(CREATIVE_LIBRARY_REPORT_ID),
        field: FIELDS.CUSTOM_CREATIVE_NAME.field,
        type: [leftPinnedColumn, editableColumn],
        maxWidth: 260,
        cellRenderer: EllipsisCellRenderer,
        cellRendererParams: { lines: 2 },
        cellEditor: FIELDS.CUSTOM_CREATIVE_NAME.cellEditor,
        valueChangeHandler: updateLibraryCreative,
        valueFormatter: FIELDS.CUSTOM_CREATIVE_NAME.valueFormatter,
        accountConfigurations: { [FEATURE_FLAGS.SHOW_CUSTOM_DATA_IN_CREATIVE_FORM]: 'true' },
    },
    {
        headerName: FIELDS.APPROVAL_STATUS.label,
        headerValueGetter: FormattedMessageHeaderRenderer,
        field: FIELDS.APPROVAL_STATUS.field,
        type: [leftPinnedColumn],
        cellRenderer: FIELDS.APPROVAL_STATUS.cellRenderer,
        cellRendererParams: FIELDS.APPROVAL_STATUS.renderParams,
        headerComponentParams: defaultHeaderComponentParams,
        csvValueGetter: FIELDS.APPROVAL_STATUS.csvValueGetter,
    },
    {
        headerName: FIELDS.ID.label,
        field: FIELDS.ID.field,
        type: [numericColumn],
        cellRenderer: CreativeIdCellRenderer,
        headerValueGetter: FormattedMessageHeaderRenderer,
        headerComponentParams: defaultHeaderComponentParams,
        cellRendererParams: { isSearchable: true },
    },
    {
        headerName: FIELDS.CONTENT.label,
        headerValueGetter: FormattedMessageHeaderRenderer,
        headerComponentParams: nonDefaultHeaderComponentParams(CREATIVE_LIBRARY_REPORT_ID),
        type: [bypassMetadataFilterType],
        valueGetter: ({ data }) => {
            const { title, description, cta } = data;
            return {
                title,
                description,
                cta,
            };
        },
        field: FIELDS.CONTENT.field,
        cellRenderer: ContentCellRenderer,
        cellRendererParams: {
            ctaMsgIdPrefix: 'creative.creator.content.cta.option',
            isSearchable: true,
        },
        width: 328,
        csv: [
            {
                headerName: 'Title',
                field: 'title',
            },
            {
                headerName: 'Description',
                field: 'description',
            },
            ...FIELDS.CTA.csv,
        ],
    },
    {
        headerName: FIELDS.AD_GROUPS_ASSIGNMENT.label,
        field: FIELDS.AD_GROUPS_ASSIGNMENT.field,
        cellRenderer: AssignmentCellRenderer,
        headerValueGetter: FormattedMessageHeaderRenderer,
        cellRendererParams: { isSearchable: true },
        headerComponentParams: FIELDS.AD_GROUPS_ASSIGNMENT.headerComponentParams,
        cellEditor: FIELDS.AD_GROUPS_ASSIGNMENT.cellEditor,
        valueFormatter: FIELDS.AD_GROUPS_ASSIGNMENT.valueFormatter,
    },
    {
        headerName: FIELDS.FORMAT.label,
        field: FIELDS.FORMAT.field,
        cellRendererParams: FIELDS.FORMAT.renderParams,
        cellRenderer: FIELDS.FORMAT.cellRenderer,
        csvValueGetter: FIELDS.FORMAT.csvValueGetter,
        valueGetter: ({ data }) => {
            const { format } = data;
            if (isHTMLCardCreative(data)) {
                return data.displayData.displayAdType;
            }
            return format;
        },
        visibilityCondition: ({ selectedAccount, selectedAccountConfig }) =>
            isFeatureAllowedForICPAccount(
                selectedAccount,
                selectedAccountConfig,
                FEATURE_FLAGS.SHOULD_SHOW_DISPLAY_DATA
            ),
    },
    {
        headerName: FIELDS.DIMENSIONS.label,
        field: FIELDS.DIMENSIONS.field,
        cellRenderer: FIELDS.DIMENSIONS.cellRenderer,
        valueFormatter: FIELDS.DIMENSIONS.valueFormatter,
        valueGetter: FIELDS.DIMENSIONS.valueGetter,
        csv: FIELDS.DIMENSIONS.csv,
        visibilityCondition: ({ selectedAccount, selectedAccountConfig }) =>
            isFeatureAllowedForICPAccount(
                selectedAccount,
                selectedAccountConfig,
                FEATURE_FLAGS.SHOULD_SHOW_DISPLAY_DATA
            ),
    },
    {
        headerName: FIELDS.CREATIVE_ORIENTATION.label,
        field: FIELDS.CREATIVE_ORIENTATION.field,
        cellRendererParams: FIELDS.CREATIVE_ORIENTATION.renderParams,
        cellRenderer: FIELDS.CREATIVE_ORIENTATION.cellRenderer,
        csvValueGetter: FIELDS.CREATIVE_ORIENTATION.csvValueGetter,
    },
    {
        headerName: FIELDS.CREATE_TIME.label,
        headerValueGetter: FormattedMessageHeaderRenderer,
        headerComponentParams: FIELDS.CREATE_TIME.headerComponentParams,
        field: FIELDS.CREATE_TIME.field,
        valueFormatter: FIELDS.CREATE_TIME.valueFormatter,
        csvValueGetter: FIELDS.CREATE_TIME.csvValueGetter,
    },
    {
        headerName: FIELDS.URL.label,
        headerValueGetter: FormattedMessageHeaderRenderer,
        headerComponentParams: FIELDS.URL.headerComponentParams,
        field: FIELDS.URL.field,
        type: [editableColumn, keyColumn],
        cellRenderer: FIELDS.URL.cellRenderer,
        cellEditor: FIELDS.URL.cellEditor,
        cellEditorParams: {
            validations: urlValidations,
        },
        valueChangeHandler: updateLibraryCreativeUrlCellEditor,
        cellRendererParams: { isSearchable: true },
        valueSetter: params => {
            params.data[FIELDS.URL.field] = params.newValue.url;
            params.data[BYPASS_URL_RESPONSE_VALIDATION_FIELD] = params.newValue[BYPASS_URL_RESPONSE_VALIDATION_FIELD];
            return true;
        },
    },
    {
        headerName: FIELDS.BRANDING_TEXT.label,
        field: FIELDS.BRANDING_TEXT.field,
        headerValueGetter: FormattedMessageHeaderRenderer,
        cellRenderer: EllipsisCellRenderer,
        headerComponentParams: defaultHeaderComponentParams,
    },
    {
        headerName: FIELDS.TYPE.label,
        field: FIELDS.TYPE.field,
        cellRendererParams: { msgIdPrefix: 'app.campaigns.creative-library.channel.type' },
        cellRenderer: FIELDS.TYPE.cellRenderer,
        csvValueGetter: FIELDS.TYPE.csvValueGetter,
        visibilityCondition: ({ selectedAccount, selectedAccountConfig }) =>
            isFeatureAllowedForICPAccount(
                selectedAccount,
                selectedAccountConfig,
                FEATURE_FLAGS.SHOULD_SHOW_DISPLAY_DATA
            ),
    },
    {
        headerName: FIELDS.ACTIONS_COLUMN.label,
        field: FIELDS.ACTIONS_COLUMN.field,
        type: [rightPinnedColumn, actionsColumnType, staticColumn],
        minWidth: 128,
        maxWidth: 128,
        width: 128,
        cellRendererParams: {
            onDelete: deleteLibraryCreative,
            onPreview: previewLibraryCreative,
            onEdit: editLibraryCreative,
            onDuplicate: duplicateLibraryCreative,
        },
        cellRenderer: CreativeLibraryActionsCellRenderer,
        csv: false,
    },
];

export const availableColumnDefinitions = normalizeColumnDefs(
    creativeLibraryReportColumnDefinition,
    CREATIVE_LIBRARY_REPORT_ID,
    withCreativeEditIndication
);
